import React, { useRef } from "react";
import { Container, Graphics } from "@pixi/react";
import SpineAnimation from "../../../Elements/Textures/SpineAnimation";
import Jackpots from "../../../Components/UI/Jackpots/Jackpots";
import { Settings } from "../../../settings";
import { ASSET_NAME } from "../../../assets";
import BasicTexture from "../../../Elements/Textures/BasicTexture";
import WinAnimation from "../../../Components/Animations/WinAnimation/WinAnimation";
import WinsDisplay from "../../../Components/UI/WinsDisplay/WinsDisplay";
import { useSelector } from "react-redux";

// MainReelsetBackground component provides the visual background for the reel set.
const MainReelsetBackground = ({ dimensions, symbolSize, children }) => {
  // Accessing the Redux store to get the simulation mode and game type settings.
  const { isFreeSpinMode } = useSelector((state) => state.freeSpins);
  // Creating a reference for the mask used in the Graphics component.
  const maskRef = useRef(null);

  return (
    <Container
      name="ReelSetBackground"
      position={{ x: dimensions.width / 2, y: dimensions.height / 2 }}
      zIndex={1}
      sortableChildren
    >
      {/* Conditionally render WinAnimation if the game type is "regular". */}
      {!isFreeSpinMode && (
        <WinAnimation dimensions={dimensions} symbolSize={symbolSize} />
      )}

      {/* Conditionally render Jackpots if it is enabled in settings. */}

      {Settings.JACKPOT_FEATURE && (
        <Jackpots
          symbolSize={symbolSize}
          dimensions={dimensions}
          y={-symbolSize * 2}
          zIndex={3}
        />
      )}

      {/* Render a SpineAnimation component for visual effects. */}
      <SpineAnimation
        zIndex={1}
        animationName="Shine"
        x={-symbolSize * 3}
        width={(symbolSize * Settings.REEL_COUNT) / 2}
        height={symbolSize * Settings.SYMBOLS_VISIBLE}
        animationPath={ASSET_NAME.background}
        y={0}
      />

      {/* Render a BasicTexture component for the lines landscape. */}
      <BasicTexture
        textureName="linesLandscape"
        width={symbolSize / 2}
        height={symbolSize / 2}
        zIndex={1}
        x={-symbolSize * 3}
      />

      {/* Render a BasicTexture component for the reel frame background. */}
      <BasicTexture
        textureName="reelFrameBack"
        width={symbolSize * Settings.REEL_COUNT}
        height={symbolSize * Settings.SYMBOLS_VISIBLE}
        zIndex={2}
      />

      {/* Graphics component used to create a mask for the reel set. */}
      <Graphics
        ref={maskRef}
        draw={(g) => {
          g.clear();
          g.beginFill(0xffffff);
          g.drawRect(
            -(symbolSize * Settings.REEL_COUNT) / 2,
            -(symbolSize * Settings.SYMBOLS_VISIBLE) / 2,
            symbolSize * Settings.REEL_COUNT,
            symbolSize * Settings.SYMBOLS_VISIBLE
          );
          g.endFill();
        }}
      />

      {/* Container that uses the mask created above to clip its children. */}
      <Container
        name={"MainReelSetContainer"}
        mask={maskRef.current}
        x={0}
        y={0}
        sortChildren
        zIndex={3}
      >
        {children}
      </Container>
      {/* Render a BasicTexture component for the main reel frame front. */}
      <BasicTexture
        textureName="mainReelFrameFront"
        width={symbolSize * 1.027 * Settings.REEL_COUNT}
        height={symbolSize * 1.04 * Settings.SYMBOLS_VISIBLE}
        zIndex={3}
      />

      {/* Render another BasicTexture component for the lines landscape on the opposite side. */}
      <BasicTexture
        textureName="linesLandscape"
        width={symbolSize / 2}
        height={symbolSize / 2}
        zIndex={2}
        x={symbolSize * 3}
      />

      {/* Render another SpineAnimation component for visual effects on the opposite side. */}
      <SpineAnimation
        zIndex={1}
        animationName="Shine"
        x={symbolSize * 3}
        width={(symbolSize * Settings.REEL_COUNT) / 2}
        height={symbolSize * Settings.SYMBOLS_VISIBLE}
        animationPath={ASSET_NAME.background}
        y={0}
      />

      {/* Render a WinsDisplay component to show the wins. */}

      <WinsDisplay
        dimensions={dimensions}
        width={symbolSize * Settings.REEL_COUNT}
        height={symbolSize}
        zIndex={5}
        y={symbolSize * Settings.SYMBOLS_VISIBLE - symbolSize}
        x={0}
      />
    </Container>
  );
};

// Export the MainReelsetBackground component for use in other parts of the application.
export default MainReelsetBackground;
